import { toast } from '../ui/Overlays';

/**
 * What reaches a courier or a customer waits 5 seconds with Undo (control-room-web.html, "Actions": `hold`). The page
 * shows it at once; Undo inside the 5 seconds means it was never sent; after them it goes. Another action sends the one
 * waiting at once rather than dropping it. Closing or reloading the page while something waits or is still being sent
 * asks first (the browser's own "Leave site?") and starts sending it; signing out waits for it (`settle`).
 */

interface Held {
  send: () => Promise<void>;
  t: ReturnType<typeof setTimeout>;
}

let held: Held | null = null;
/** Sends under way: timed through Routr's server, then written. */
const running = new Set<Promise<void>>();
/** What an earlier action has to say while another waits with its Undo: said once that one is sent or undone. */
let later: { msg: string; ms?: number } | null = null;

/** How long a send waits for its Undo. */
export const HOLD_MS = 5000;

/** Says [msg] in the toast, unless an action is waiting with its Undo: then once that one is sent or undone. */
export function say(msg: string, ms?: number): void {
  if (held) later = { msg, ms };
  else toast(msg, ms ? { ms } : {});
}

function sayLater(after = 0) {
  const l = later;
  later = null;
  if (!l) return;
  setTimeout(() => {
    if (held) later = l;
    else toast(l.msg, l.ms ? { ms: l.ms } : {});
  }, after);
}

/** What to say when a write fails: the same words every time, or words for what went wrong. */
export type Failed = string | ((e: unknown) => string);

function run(send: () => Promise<void>, fail: () => void, failed: Failed): Promise<void> {
  const p = (async () => {
    try {
      await send();
    } catch (e) {
      console.warn(e);
      fail();
      say(typeof failed === 'string' ? failed : failed(e), 6000);
    }
  })();
  running.add(p);
  void p.finally(() => running.delete(p));
  return p;
}

/**
 * [msg] shows with Undo for 5 seconds; then [send] runs. [undo] puts the page back as it was. [fail] does the same if
 * the write didn't go through, and [failed] says so. [sent] is said once it's written; a send can also `say` its own.
 */
export function hold(msg: string, o: { send: () => Promise<void>; undo: () => void; failed?: Failed; sent?: string }): void {
  void flushHold();
  const failed = o.failed ?? 'Couldn’t send it. Check your connection and try again.';
  const go = () =>
    run(
      async () => {
        await o.send();
        if (o.sent) say(o.sent);
      },
      o.undo,
      failed,
    );
  const h: Held = {
    send: go,
    t: setTimeout(() => {
      if (held === h) held = null;
      void go();
      sayLater();
    }, HOLD_MS),
  };
  held = h;
  toast(msg, {
    ring: true,
    ms: HOLD_MS + 200,
    undo: () => {
      if (held !== h) return 'It’s already sent';
      clearTimeout(h.t);
      held = null;
      o.undo();
      // After the toast's own "Undone".
      sayLater(1700);
      return null;
    },
  });
}

/** Sends what's waiting now (another action, the page closing, signing out); done when it's written. */
export function flushHold(): Promise<void> {
  if (!held) return Promise.resolve();
  clearTimeout(held.t);
  const h = held;
  held = null;
  return h.send();
}

/** Everything waiting or being sent, written (or failed): signing out waits for this. */
export async function settle(): Promise<void> {
  await flushHold();
  while (running.size) await Promise.allSettled([...running]);
}

/** An action that writes at once (no one is told), with the page back as it was if it fails. */
export function now(o: { write: () => Promise<unknown>; undo?: () => void; failed?: string; done?: string }): void {
  void run(
    async () => {
      await o.write();
      if (o.done) say(o.done);
    },
    o.undo ?? (() => {}),
    o.failed ?? 'Couldn’t save that. Check your connection and try again.',
  );
}

addEventListener('beforeunload', (e) => {
  if (!held && !running.size) return;
  void flushHold();
  e.preventDefault();
  e.returnValue = '';
});
addEventListener('pagehide', () => void flushHold());
